import { uploadRecovery } from './uploadRecovery';
import { FREE_UPLOAD_LIMIT_BYTES, PREMIUM_UPLOAD_LIMIT_BYTES } from './uploadLimits';
import { importMeasurements } from './sourceImport';

export function isSavedUpgrade(payload, status) {
  return status === 402 && payload?.requires_upgrade === true
    && typeof payload.id === 'string' && payload.id.length > 0;
}

const allowedErrorCodes = new Set(['auth_verification_unavailable','guest_daily_limit','daily_limit_reached',
  'guest_pending_upload_limit','guest_session_expired','guest_session_ended','guest_signup_rate_limit',
  'source_invalid_url','source_not_public','source_access_denied','source_not_found','source_unsupported_media','source_folder_or_playlist',
  'source_redirect_failed','source_connection_failed','source_timeout','source_rate_limited','source_import_unavailable',
  'source_import_failed','source_geo_restricted','file_too_large']);

export function importFailure(payload, status, urlType, error) {
  if (!payload?.code && allowedErrorCodes.has(error?.code)) payload = { ...payload, code: error.code };
  const recovery = uploadRecovery(payload, status);
  if (recovery?.kind === 'auth_temporary') return {
    code: recovery.code, retryable: true, messageKey: recovery.messageKey, kind: 'auth_temporary',
  };
  if (recovery && recovery.code !== 'daily_limit_reached') return {
    code: recovery.code, retryable: false, messageKey: recovery.messageKey,
    kind: recovery.kind === 'daily_limit' || recovery.kind === 'pending_limit' ? 'plan_limit'
      : recovery.kind === 'session' ? 'auth' : 'rate_limit',
  };
  const apiError = payload?.error && typeof payload.error === 'object' ? payload.error : {};
  const message = String(apiError.message || payload?.error || error?.message || '').toLowerCase();
  let code = payload?.code || apiError.code;
  const allowed = new Set(['invalid_youtube_url','youtube_captions_missing','youtube_original_captions_missing',
    'youtube_video_unavailable','youtube_connection_failed','youtube_timeout','youtube_import_failed','daily_limit_reached','file_too_large',
    'source_invalid_url','source_not_public','source_access_denied','source_not_found','source_unsupported_media','source_folder_or_playlist',
    'source_redirect_failed','source_connection_failed','source_timeout','source_rate_limited','source_import_unavailable',
    'source_import_failed','source_geo_restricted','unsupported_format','upload_missing_source']);
  if (!allowed.has(code)) code = undefined;
  // Older servers also used "not public" for an unexplained upstream 403.
  if (code === 'source_not_public' && payload?.source_status === 403) code = 'source_access_denied';
  // Compatibility with the previous backend during a rolling release.
  if (!code && urlType === 'youtube') {
    if (message.includes('original audio language')) code = 'youtube_original_captions_missing';
    else if (message.includes('captions available') || message.includes('usable captions')) code = 'youtube_captions_missing';
    else if (message.includes('unavailable') || message.includes('private')) code = 'youtube_video_unavailable';
    else if (message.includes('valid youtube') || message.includes('single youtube')) code = 'invalid_youtube_url';
  }
  if (!code && status === 413) code = 'file_too_large';
  if (!code && (payload?.requires_upgrade || message.includes('limit') || message.includes('maximum'))) code = 'plan_limit';
  if (!code && error?.code === 'request_timeout') code = 'source_timeout';
  if (!code && (status >= 500 || error?.name === 'TypeError')) code = 'source_connection_failed';
  if (!code && [401,403].includes(status)) code = 'import_access_denied';
  code ||= 'source_import_failed';
  const retryable = ['youtube_connection_failed','youtube_timeout','source_connection_failed','source_timeout','source_rate_limited','source_import_unavailable'].includes(code);
  const kind = ['daily_limit_reached','plan_limit','file_too_large'].includes(code) ? 'plan_limit'
    : retryable ? 'network' : 'input';
  const copy = {
    youtube_captions_missing: 'captionsMissing', youtube_original_captions_missing: 'originalCaptionsMissing',
    youtube_video_unavailable: 'videoUnavailable', invalid_youtube_url: 'invalidVideo',
    youtube_connection_failed: 'connectionFailed', source_connection_failed: 'connectionFailed',
    youtube_timeout:'sourceTimeout',
    daily_limit_reached: 'dailyLimit', plan_limit: 'planLimit', file_too_large: 'fileTooLarge',
    import_access_denied: 'accessDenied',
    source_invalid_url: 'invalidSource', source_not_public: 'sourceNotPublic', source_access_denied: 'sourceAccessDenied', source_not_found: 'sourceNotFound',
    source_unsupported_media: 'unsupportedMedia', unsupported_format: 'unsupportedMedia',
    source_folder_or_playlist: 'singleFile', source_redirect_failed: 'redirectFailed',
    source_timeout: 'sourceTimeout', source_rate_limited: 'sourceRateLimited',
    source_import_unavailable: 'sourceUnavailable', upload_missing_source: 'invalidSource',
    source_geo_restricted: 'soundCloudRegion',
  };
  const result = { code, kind, retryable, messageKey: `import.${copy[code] || 'sourceFailed'}`, analytics: importMeasurements(payload) };
  if (payload?.source_family === 'soundcloud') {
    if (code === 'source_not_public') result.messageKey = 'import.soundCloudNotPublic';
    if (code === 'source_not_found') result.messageKey = 'import.soundCloudNotFound';
    if (code === 'source_unsupported_media') result.messageKey = 'import.soundCloudNoAudio';
  }
  if ((payload?.source_family === 'google_drive' || urlType === 'google_drive')
      && ['source_not_public', 'source_access_denied'].includes(code)) {
    result.messageKey = 'import.driveAccess';
    result.driveHelp = true;
    result.retryable = true;
  }
  if (code === 'source_unsupported_media') {
    if (payload?.source_family === 'google_drive') result.messageKey = 'import.driveMedia';
    else if (payload?.source_family === 'unsupported_platform') result.messageKey = 'import.platformMedia';
    else if (payload?.content_class === 'html') result.messageKey = 'import.webpageMedia';
  }
  if (code === 'file_too_large') {
    const freeLimit = payload?.max_size_bytes === FREE_UPLOAD_LIMIT_BYTES;
    const size = payload?.file_size_bytes;
    const knownSize = Number.isSafeInteger(size) && size > 0;
    result.upgradeAvailable = freeLimit && payload?.upgrade_available === true && knownSize
      && size > FREE_UPLOAD_LIMIT_BYTES && size <= PREMIUM_UPLOAD_LIMIT_BYTES
      && payload?.premium_max_size_bytes === PREMIUM_UPLOAD_LIMIT_BYTES;
    if (knownSize && size > PREMIUM_UPLOAD_LIMIT_BYTES) result.messageKey = 'import.abovePremiumLimit';
    else if (freeLimit) result.messageKey = 'import.freeFileTooLarge';
    else if (payload?.max_size_bytes === PREMIUM_UPLOAD_LIMIT_BYTES) result.messageKey = 'import.premiumFileTooLarge';
  }
  return result;
}
